import { registerTypefaces } from '@zeiss/zui-v/major';

import { API_ENDPOINTS } from './api-endpoints';
import { createLayout } from './app-layout';
import {
  fetchAndStoreSessionContext,
  fetchAndStoreSessionData,
  userShouldAuthenticate,
  zeissIdLogin,
} from './auth';
import { setCsrfForFetch, setCsrfForXHR } from './auth/csrf-interceptors';
import { connectRouteHandler } from './connect/connect-route-handler';
import { installConsentScript } from './consent';
import { createApp } from './create-app';
import { getPublicPath } from './environment';
import { handleNotificationPermissionPromptAfterLogin } from './notifications/notification-subscription';
import { renderApp } from './render';
import { instructions } from './routes';
import { setupTracking } from './tracking/init';
import {
  flushLoginWelcomePagePerformanceEvent,
  updateLoginStartOnSessionConfirmed,
  waitForAppShellReady,
} from './tracking/login-welcome-page-performance';
import type { AppConfig, RuntimeConfig, UserDevice } from './types';
import { patchBrowserApi } from './utils/browser-api-patch';
import { resolveClientType } from './utils/client';
import { initFetchJSON } from './utils/fetch-json';
import { initTechLogger } from './utils/logging';
import { redirectPubliclyAccessibleRoutes } from './utils/public-pages';
import { startSessionRefreshStream } from './utils/session-refresh';

export async function setupApp(appConfig: AppConfig, deviceType: UserDevice): Promise<void> {
  const config: RuntimeConfig = {
    ...appConfig,
    proxy: API_ENDPOINTS.proxy,
    endpoints: API_ENDPOINTS,
  };

  const cjkFonts = ['Noto Sans KR', 'Noto Sans TC', 'Noto Sans JP'];
  await Promise.all(
    cjkFonts.map((font) => registerTypefaces(`${getPublicPath()}assets/fonts`, font)),
  );

  setCsrfForFetch(config.auth.csrfCookieName, config.auth.csrfHeaderName);
  setCsrfForXHR(config.auth.csrfCookieName, config.auth.csrfHeaderName);

  initFetchJSON(config);
  initTechLogger(config);
  document.title = config.title;

  const [sessionInfo] = await Promise.all([
    fetchAndStoreSessionData(config),
    fetchAndStoreSessionContext(config),
  ]);

  // Prompt user for notification permission after login if the user has not yet granted or denied the browser to send notifications
  handleNotificationPermissionPromptAfterLogin();

  if (sessionInfo) {
    // Record the start time once both session data and session context have been fetched —
    // spaOnlyLoadDurationMs will cover layout creation, pilet loading, and the full render
    // up to the Welcome Page.
    updateLoginStartOnSessionConfirmed();
  }

  if (await connectRouteHandler(config, sessionInfo)) {
    return;
  }

  if (sessionInfo) {
    startSessionRefreshStream(config, sessionInfo.sessionExpirationInSeconds);
  }

  const clientType = resolveClientType(deviceType);

  redirectPubliclyAccessibleRoutes(clientType);

  if (userShouldAuthenticate()) {
    await zeissIdLogin(config);
    return;
  }

  // Don't install (and show) Data privacy settings dialog on instructions page
  if (location.pathname !== `${getPublicPath()}${instructions.substring(1)}`) {
    installConsentScript(config.oneTrust.id);
  }

  if (config.tracking.enabled) {
    setupTracking(config);
  }

  // The following code is only reached initially if user is authenticated (the page will be refreshed otherwise)
  const app = createApp(config);
  const layout = await createLayout(app, clientType);

  const instance = renderApp(app, layout);

  patchBrowserApi();

  document.getElementById('loading-container')?.remove();

  if (config.tracking.enabled && instance) {
    waitForAppShellReady(instance)
      .then(() => flushLoginWelcomePagePerformanceEvent())
      .catch((error) => {
        console.error('Failed to measure performance', error);
      });
  }
}
